import { InjectionToken } from '@angular/core';
import type {
  OgcOnboardingMode,
  ResourceServerId,
} from './dataset-home/dataset-home.models';

/** Catalogue item API default resource server id (same across MahaAgX flows). */
export const DEFAULT_CATALOGUE_RESOURCE_SERVER_ID = 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1';

/** Host-only resource server URLs for catalogue `resourceServer[].url` (no protocol). */
export interface ResourceServerUrlConfig {
  'ngsi-ld': string;
  gateway: string;
  ogc: string;
  'file-server': string;
}

/** Environment keys used to resolve catalogue resource-server URLs. */
export interface ResourceServerUrlConfigSource {
  ngsiLdApiPath?: string;
  baseRsPathUrl?: string;
  gatewayApiPath?: string;
  ogcMapsResourceServerUrl?: string;
  baseFilePathUrl?: string;
}

export const RESOURCE_SERVER_URL_CONFIG = new InjectionToken<ResourceServerUrlConfig>(
  'RESOURCE_SERVER_URL_CONFIG',
);

/** Catalogue item `apdURL` (host + `/acl`, no protocol). */
export const CATALOGUE_APD_URL = new InjectionToken<string>('CATALOGUE_APD_URL');

/** Strip protocol and trailing slash — matches catalogue `resourceServer.url` storage. */
export function normalizeResourceServerHostUrl(url: string): string {
  return url.trim().replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

/** Resolve catalogue `apdURL` from controlplane `basePathUrl` (e.g. `v2.dev.controlplane.iudx.io/acl`). */
export function resolveCatalogueApdUrl(basePathUrl: string | undefined): string {
  const host = normalizeResourceServerHostUrl(basePathUrl || '');
  return host ? `${host}/acl` : '';
}

/** Build host-only URLs from app environment for each onboarding resource server. */
export function resolveResourceServerUrlConfig(
  source: ResourceServerUrlConfigSource,
): ResourceServerUrlConfig {
  const ngsild = normalizeResourceServerHostUrl(
    source.ngsiLdApiPath || source.baseRsPathUrl || '',
  );
  const gatewayFromEnv = normalizeResourceServerHostUrl(source.gatewayApiPath || '');
  const gateway =
    gatewayFromEnv.includes('/rsp') ? gatewayFromEnv : ngsild ? `${ngsild}/rsp` : gatewayFromEnv;

  return {
    'ngsi-ld': ngsild,
    gateway,
    ogc: normalizeResourceServerHostUrl(source.ogcMapsResourceServerUrl || ''),
    'file-server': normalizeResourceServerHostUrl(source.baseFilePathUrl || ''),
  };
}

const RS_PAYLOAD_META: Record<
  ResourceServerId,
  { name: string; type: string; accessTypes: readonly string[] }
> = {
  'ngsi-ld': { name: 'NGSI-LD', type: 'ngsi-ld', accessTypes: ['api'] },
  gateway: { name: 'GATEWAY', type: 'gateway', accessTypes: ['api'] },
  ogc: { name: 'OGC', type: 'ogc', accessTypes: ['api'] },
  'file-server': { name: 'FILE', type: 'file', accessTypes: ['file'] },
};

/** Catalogue `resourceServer[].accessTypes` for the chosen server (API vs FILE). */
export function resolveResourceServerAccessTypes(
  resourceServerId: ResourceServerId | string | null | undefined,
): string[] {
  const id = normalizeResourceServerId(resourceServerId);
  return [...RS_PAYLOAD_META[id].accessTypes];
}

export function buildResourceServerPayloadEntry(
  resourceServerId: ResourceServerId,
  urlConfig: ResourceServerUrlConfig,
  overrides?: Partial<{ id: string; url: string }>,
  ogcOnboardingMode?: 'feature' | 'raster' | null,
): Record<string, unknown> {
  const id = normalizeResourceServerId(resourceServerId);
  const meta = RS_PAYLOAD_META[id];
  const url = overrides?.url?.trim()
    ? normalizeResourceServerHostUrl(overrides.url)
    : urlConfig[id] || '';

  let queryTypes: string[];
  if (id === 'ogc' && ogcOnboardingMode === 'feature') {
    queryTypes = ['FEATURE', 'ATTR'];
  } else if (id === 'ogc' && ogcOnboardingMode === 'raster') {
    queryTypes = ['STAC', 'ATTR'];
  } else {
    queryTypes = ['ATTR'];
  }

  return {
    id: overrides?.id || DEFAULT_CATALOGUE_RESOURCE_SERVER_ID,
    name: meta.name,
    queryTypes,
    accessTypes: [...meta.accessTypes],
    type: meta.type,
    url,
  };
}

export function buildResourceServerPayloadArray(
  resourceServerId: ResourceServerId,
  urlConfig: ResourceServerUrlConfig,
  ogcOnboardingMode?: 'feature' | 'raster' | null,
): Array<Record<string, unknown>> {
  return [buildResourceServerPayloadEntry(resourceServerId, urlConfig, undefined, ogcOnboardingMode)];
}

/** Infer resource server id from form value (label, id, or full URL). */
export function inferResourceServerIdFromValue(value: string): ResourceServerId {
  const v = value.toLowerCase();
  if (v === 'file' || v.includes('file-s3') || v.includes('files-connect')) {
    return 'file-server';
  }
  if (v.includes('ogc') || v.includes('geoserver')) {
    return 'ogc';
  }
  if (v.includes('/rsp') || v.includes('gateway') || v.includes('rs-proxy')) {
    return 'gateway';
  }
  if (v.includes('ngsi') || v.includes('.rs.') || v.includes('dataplane')) {
    return 'ngsi-ld';
  }
  return normalizeResourceServerId(value);
}

export interface ResourceServerDisplayInfo {
  id: ResourceServerId;
  title: string;
  icon: 'network' | 'files' | 'globe' | 'server';
}

export const RESOURCE_SERVER_DISPLAY: Record<ResourceServerId, ResourceServerDisplayInfo> = {
  'ngsi-ld': {
    id: 'ngsi-ld',
    title: 'NGSI-LD Server',
    icon: 'network',
  },
  'file-server': {
    id: 'file-server',
    title: 'File Server',
    icon: 'files',
  },
  ogc: {
    id: 'ogc',
    title: 'OGC Server',
    icon: 'globe',
  },
  gateway: {
    id: 'gateway',
    title: 'GATEWAY Server',
    icon: 'server',
  },
};

export function getResourceServerDisplay(
  id: ResourceServerId | string | null | undefined,
): ResourceServerDisplayInfo {
  const normalized = normalizeResourceServerId(id);
  return RESOURCE_SERVER_DISPLAY[normalized];
}

/** Connection Details modal intro copy — matches tabs from upload-files by resource server. */
export type ConnectionDetailsModalLedeKind =
  | 'data-source-readonly'
  | 'file-server'
  | 'gateway'
  | 'direct-push'
  | 'sftp-api';

export function getConnectionDetailsModalLedeKind(
  resourceServerId: ResourceServerId | string | null | undefined,
  options?: { dataSourceReadOnly?: boolean; directPushOnly?: boolean },
): ConnectionDetailsModalLedeKind {
  if (options?.dataSourceReadOnly) {
    return 'data-source-readonly';
  }
  if (options?.directPushOnly) {
    return 'direct-push';
  }
  switch (normalizeResourceServerId(resourceServerId)) {
    case 'file-server':
      return 'file-server';
    case 'gateway':
      return 'gateway';
    default:
      return 'sftp-api';
  }
}

export function ogcOnboardingModeLabel(mode: OgcOnboardingMode | null | undefined): string {
  if (mode === 'feature') return 'Feature Onboarding';
  if (mode === 'raster') return 'Raster Onboarding';
  return '';
}

/** Map API / UI labels to onboarding resource server id. */
export function normalizeResourceServerId(
  value: string | ResourceServerId | null | undefined,
): ResourceServerId {
  const raw = (value ?? '').toString().trim();
  if (!raw) return 'ngsi-ld';

  const v = raw.toLowerCase().replace(/\s+/g, '-');
  if (v === 'ngsi-ld' || v === 'ngsild' || v === 'ngsi_ld') return 'ngsi-ld';
  if (v === 'file-server' || v === 'file' || v === 'files') return 'file-server';
  if (v === 'gateway') return 'gateway';
  if (v === 'ogc') return 'ogc';

  const upper = raw.toUpperCase();
  if (upper === 'NGSI-LD' || upper.includes('NGSI')) return 'ngsi-ld';
  if (upper === 'FILE') return 'file-server';
  if (upper === 'GATEWAY') return 'gateway';
  if (upper === 'OGC') return 'ogc';

  return 'ngsi-ld';
}

/** Unwrap common API envelope shapes (`result`, `data.result`, etc.). */
export function unwrapCatalogueApiResult(
  response: unknown,
): Record<string, unknown> | null {
  if (!response || typeof response !== 'object') return null;
  const root = response as Record<string, unknown>;
  const result = root['result'];
  if (result && typeof result === 'object') {
    return result as Record<string, unknown>;
  }
  const data = root['data'];
  if (data && typeof data === 'object') {
    const dataObj = data as Record<string, unknown>;
    const nested = dataObj['result'];
    if (nested && typeof nested === 'object') {
      return nested as Record<string, unknown>;
    }
    return dataObj;
  }
  return root;
}

export function resourceServerKeysFromPayload(
  payload: Record<string, unknown> | null,
): string[] {
  if (!payload) return [];
  const resourceServers =
    (payload['resourceServers'] as Record<string, unknown> | undefined) ??
    (payload['resource_servers'] as Record<string, unknown> | undefined);
  if (resourceServers && typeof resourceServers === 'object' && !Array.isArray(resourceServers)) {
    return Object.keys(resourceServers);
  }
  const resourceServer =
    payload['resourceServer'] ?? payload['resource_server'];
  if (Array.isArray(resourceServer)) {
    return resourceServer
      .map((rs) => {
        if (rs && typeof rs === 'object') {
          return String((rs as Record<string, unknown>)['name'] ?? '');
        }
        return String(rs ?? '');
      })
      .filter(Boolean);
  }
  return [];
}

export function resourceServerIdFromKeys(keys: string[]): ResourceServerId | null {
  for (const key of keys) {
    const id = normalizeResourceServerId(key);
    if (id) return id;
  }
  return null;
}

/** Read resource server type from create-item API `result.resourceServers` keys. */
export function resourceServerIdFromCreateItemResponse(
  response: unknown,
): ResourceServerId | null {
  const result = unwrapCatalogueApiResult(response);
  const keys = resourceServerKeysFromPayload(result);
  return resourceServerIdFromKeys(keys);
}

/** Read resource server from item GET / item detail payload. */
export function resourceServerIdFromItemDetail(
  response: unknown,
): ResourceServerId | null {
  const result = unwrapCatalogueApiResult(response);
  const keys = resourceServerKeysFromPayload(result);
  if (keys.length > 0) {
    return resourceServerIdFromKeys(keys);
  }
  return null;
}
